/* eslint-disable react/jsx-props-no-spreading */
import { Suspense, useState } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { isAuthenticated } from 'actions/user';
import { showComponentOnSelectedRoute } from 'components/app/helpers';
import Render from 'components/render';
import { SHOW_SCALPER_OFFER_STRIP_ROUTES } from 'constants/constants';
import { STORAGE_KEYS } from 'constants/enums';
import { SCALPING_ENABLED } from 'constants/featureFlag';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { URL_SEARCH_PARAMS_KEYS } from 'constants/urlSearchParams';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { useChartTabs } from 'hooks/useChartTabs';
import useLocalStorage from 'hooks/useLocalStorage';
import { useRouterSearchParams } from 'hooks/useRouterSearchParams';
import { scalpingDataSelector, userSelector } from 'selectors/otherSelectors';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppSelector } from 'storeHooks';
import { SettingIcon } from 'styles/assets/icons';
import { ContractType } from 'types/IProducts';
import Box from 'UIKit/Box';
import { UnderlineTab, UnderlineTabs } from 'UIKit/DeltaUnderlineTabs';

import { CHART_ORDERBOOK_SECTION_TABS } from './ChartOrderbookTab.constants';
import classes from './ChartOrderbookTab.module.scss';
import ChartTypeDropdown from './chartTypeDropdown';
import { Chart, OrderBook, RecentTrade } from './components';
import SettingsModal from './SettingsModal';

const ChartOrderBookTab = () => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation(['trading']);
  const searchParams = useRouterSearchParams();
  const { activeTab: chartActiveTab } = useChartTabs();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const user = useAppSelector(userSelector);
  const selectedProduct = useAppSelector(selectedProductSelector);
  const isScalperOfferEnabledForUser = useAppSelector(
    state => scalpingDataSelector(state).scalperOffer?.futures_scalper_offer_enabled
  );
  const symbol = selectedProduct?.symbol;
  const productType = selectedProduct?.contract_type;

  const isLoggedIn = isAuthenticated(user);

  const showComponentOnRoute = showComponentOnSelectedRoute(
    SHOW_SCALPER_OFFER_STRIP_ROUTES
  )(location);

  const [isScalperOfferStripShown] = useLocalStorage(
    STORAGE_KEYS.SCALPER_OFFER_STRIP_SHOWN_KEY,
    true
  );

  const isScalperOfferStripVisible =
    isLoggedIn &&
    showComponentOnRoute &&
    SCALPING_ENABLED &&
    isScalperOfferStripShown &&
    !isScalperOfferEnabledForUser;

  const activeTab =
    searchParams.get(URL_SEARCH_PARAMS_KEYS.CHART_ORDERBOOK_MOBILE.SECTION_TABS) ||
    CHART_ORDERBOOK_SECTION_TABS.CHART;

  const tabList = [
    {
      tabId: CHART_ORDERBOOK_SECTION_TABS.ORDERBOOK,
      title: t('trading:tabList.orderBook'),
      'data-testid': 'mobile-orderbook-tab',
    },
    {
      tabId: CHART_ORDERBOOK_SECTION_TABS.RECENT_TRADES,
      title: t('trading:tabList.recentTrade'),
      'data-testid': 'recent-trades-tab',
    },
  ];

  const getOnTabClickRedirectionRoute = (tab: (typeof tabList)[number]) => {
    const queryParams = new URLSearchParams({
      [URL_SEARCH_PARAMS_KEYS.CHART_ORDERBOOK_MOBILE.SECTION_TABS]: tab.tabId,
    });

    return { ...location, search: queryParams.toString() };
  };

  const handleSettingsIconClick = () => {
    setIsSettingsModalOpen(true);

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MOBILE_CHART.SETTINGS_BUTTON_CLICKED, {
      source: activeTab,
    });
  };

  return (
    <Box
      className={cx(classes.chartOrderbookTabWrapper, {
        [classes.chartOrderbookTabWrapperWithStickyBanner]: isScalperOfferStripVisible,
      })}
    >
      <Box flex="flex" className={classes.tabContainer}>
        <Render when={activeTab === CHART_ORDERBOOK_SECTION_TABS.CHART}>
          <Box className={classes.chartTypeDropdownWrapper}>
            <ChartTypeDropdown />
          </Box>
        </Render>
        <UnderlineTabs
          className={`${classes.tabList} ${classes.obrtTabs}`}
          value={activeTab}
        >
          <Render when={activeTab !== CHART_ORDERBOOK_SECTION_TABS.CHART}>
            <UnderlineTab
              className={`${classes.tabButton}, ${classes.chartTab}`}
              onClick={() => {
                history.push(
                  getOnTabClickRedirectionRoute({
                    tabId: CHART_ORDERBOOK_SECTION_TABS.CHART,
                    title: t('trading:tabList.charts'),
                    'data-testid': 'mobile-chart-tab',
                  })
                );
                trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.TAB_CLICKED, {
                  tab: CHART_ORDERBOOK_SECTION_TABS.CHART,
                  product_type: productType,
                  chart_type: chartActiveTab,
                  product_name: symbol,
                });
              }}
              label={t('trading:tabList.charts')}
              key={CHART_ORDERBOOK_SECTION_TABS.CHART}
              value={CHART_ORDERBOOK_SECTION_TABS.CHART}
              data-testid="mobile-chart-tab"
            />
          </Render>
          {tabList.map(tab => {
            return (
              <UnderlineTab
                className={classes.tabButton}
                onClick={() => {
                  history.push(getOnTabClickRedirectionRoute(tab));
                  trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.TAB_CLICKED, {
                    tab: tab.tabId,
                    product_type: productType,
                    product_name: symbol,
                  });
                }}
                label={tab.title}
                key={tab.tabId}
                value={tab.tabId}
                data-testid={tab['data-testid']}
              />
            );
          })}
        </UnderlineTabs>
        <Render
          when={
            activeTab === CHART_ORDERBOOK_SECTION_TABS.CHART &&
            selectedProduct?.contract_type !== ContractType.Tracker
          }
        >
          <button
            type="button"
            data-testid="mobile-chart-orderbook-setting-button"
            className={`flex-set flex--content-center flex--align-items-center naked pointer ${classes.settingButtonContainer}`}
            onClick={handleSettingsIconClick}
          >
            <div
              className={cx(
                'flex-set flex--content-center flex--align-items-center naked',
                classes.settingButton
              )}
            >
              <SettingIcon width={16} height={16} />
            </div>
          </button>
        </Render>
      </Box>
      <Suspense fallback={null}>
        <Render when={activeTab === CHART_ORDERBOOK_SECTION_TABS.CHART}>
          <Chart />
        </Render>
        <Render when={activeTab === CHART_ORDERBOOK_SECTION_TABS.ORDERBOOK}>
          <OrderBook />
        </Render>
        <Render when={activeTab === CHART_ORDERBOOK_SECTION_TABS.RECENT_TRADES}>
          <RecentTrade />
        </Render>
      </Suspense>
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </Box>
  );
};

export default ChartOrderBookTab;
